import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectAuthUserId } from '@app/reducers/auth/auth.selectors';
import { SentryLoggingService } from '@app/services/sentryLogging.service';
import { AiOnboardingApiService } from '@app/shared/ai/ai-onboarding-api.service';
import { BusinessContext } from '@app/shared/ai/ai-onboarding.models';
import { Store } from '@ngrx/store';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { firstValueFrom } from 'rxjs';

import {
  ExtractionResult,
  ImportPlaceAddress,
  ImportSelection,
  MAX_CONTEXT_LENGTH,
  ScheduleRecommendation,
} from './onboarding-import.models';
import { EMPTY_DATA, normaliseExtraction } from './onboarding-import.normalise';

const EXTRACT_ENDPOINT = 'onboarding/extract';

const SELECTION_STORAGE_KEY = 'aiOnboardingSelection';
const PLACE_STORAGE_KEY = 'aiOnboardingPlace';
const BUSINESS_STORAGE_KEY = 'aiOnboardingBusiness';
const RECOMMENDATION_STORAGE_KEY = 'aiOnboardingRecommendation';
const OWNER_STORAGE_KEY = 'aiOnboardingOwner';

/**
 * Turns an uploaded onboarding document (PDF or image) into structured teams,
 * shift templates and employees, and holds the user's reviewed selection so the
 * remaining onboarding steps (Teams → Employees → Shifts) can pre-fill their
 * forms from it. Nothing is persisted here — the existing steps still own saving.
 *
 * Extraction posts the file + note to the server-owned `onboarding/extract`
 * endpoint, which owns the prompt and output schema, recognises the account
 * owner from the session, and returns the normalised result.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingImportService {
  #api = inject(AiOnboardingApiService);
  #sentryLogging = inject(SentryLoggingService);
  #feature = inject(FeatureService);
  #store = inject(Store);

  #authUserId = this.#store.selectSignal(selectAuthUserId);

  // Reactive flag state so the read-side gates below track it when the user
  // context resolves (or Unleash flips it) instead of freezing the boot-time
  // value — the anonymous boot context can answer `false` before it's ready.
  // When the flag is off the gates make the stash invisible (the recoverable
  // equivalent of clearing it); nothing here destructively removes the keys.
  #aiEnabled = toSignal(this.#feature.isEnabled$(featureFlag.TMP_AI_ONBOARDING), { initialValue: false });

  constructor() {
    // The stash is written pre-auth, so it can't be bound to an account at write
    // time. Bind it to the first account that consumes it and drop it if a
    // different account later appears in the same tab — otherwise one visitor's
    // business address/teams could be applied to another account signed in here.
    // The flag-off case relies on the read-side gates above (a recoverable
    // hide), not a destructive clear, so a not-yet-ready anonymous context can't
    // wipe the stash on boot.
    effect(() => {
      const userId = this.#authUserId();

      if (!userId) {
        return;
      }

      untracked(() => this.#bindStashOwner(userId));
    });
  }

  // Reviewed selection the following onboarding steps pre-fill their forms from.
  // Persisted to sessionStorage so it survives the signup → account → onboarding
  // transition (where the AI-signup flow produces it) even across a reload.
  // Every getter is flag-gated at this seam so a single check covers all
  // consumers (Teams/Employees/Shifts prefill and the stashed-place PATCH): when
  // TMP_AI_ONBOARDING is off the import state is invisible and nothing acts on it.
  #selection = signal<ImportSelection | null>(this.#readPersistedSelection());
  selection = computed(() => (this.#aiEnabled() ? this.#selection() : null));

  // Places address for the selected business, stashed at signup so onboarding can
  // apply it to the default department once the account exists. Persisted the same
  // way so it survives the signup → account → onboarding transition.
  #place = signal<ImportPlaceAddress | null>(this.#readPersistedPlace());
  place = computed(() => (this.#aiEnabled() ? this.#place() : null));

  // Places business context (types/opening hours/…) gathered during signup, so
  // the onboarding file extraction can factor in the business type and opening
  // hours when suggesting teams and shift templates. Persisted like the others.
  #businessContext = signal<BusinessContext | null>(this.#readPersistedBusinessContext());
  businessContext = computed(() => (this.#aiEnabled() ? this.#businessContext() : null));

  // The staged upload (file + note) kept in memory so returning from the review
  // step back to the upload step restores what the user had, instead of making
  // them pick the file or type the note again. Not persisted: a File can't be
  // serialised and this only needs to survive the in-tab upload↔review switch.
  #stagedFile = signal<File | null>(null);
  stagedFile = this.#stagedFile.asReadonly();

  #stagedNote = signal('');
  stagedNote = this.#stagedNote.asReadonly();

  // Recommended recurring schedule inferred at extraction time. Stashed separately
  // from the reviewed selection because it is consumed much later — on arrival at
  // the Schedule after onboarding finishes — where it is reconciled against the
  // teams/shifts/employees that were actually created and drafted onto the grid.
  // Deliberately NOT dropped by clearSelection(), so finishing onboarding (which
  // clears the selection) leaves it intact for the draft-schedule orchestrator.
  #recommendation = signal<ScheduleRecommendation | null>(this.#readPersistedRecommendation());
  recommendation = computed(() => (this.#aiEnabled() ? this.#recommendation() : null));

  /**
   * Extract structured onboarding data from an uploaded PDF or image. The
   * optional `note` is a short free-text hint about the file, forwarded to the
   * backend as context. The account owner is recognised server-side from the
   * session, so it is not sent from here.
   */
  async extract(file: File, note = ''): Promise<ExtractionResult> {
    if (!this.#aiEnabled()) {
      return { status: 'empty', data: EMPTY_DATA };
    }

    try {
      const business = this.#businessContext();

      const raw = await firstValueFrom(
        this.#api.post<unknown>(EXTRACT_ENDPOINT, {
          file: await this.#toBase64(file),
          note: note.trim().slice(0, MAX_CONTEXT_LENGTH),
          businessName: business?.businessName ?? '',
          types: business?.types ?? [],
          openingHours: business?.openingHours ?? [],
        }),
      );

      return normaliseExtraction(raw);
    } catch (error) {
      // Surface extraction failures (network, rate limits, oversized/invalid
      // payloads) instead of collapsing them into a silent error — this
      // feature's failure modes need tuning and can't be tuned blind.
      this.#sentryLogging.handleError(error);
      return { status: 'error', data: EMPTY_DATA };
    }
  }

  #clearPersistedState(): void {
    this.#selection.set(null);
    this.#place.set(null);
    this.#businessContext.set(null);
    this.#recommendation.set(null);
    this.#persistSelection(null);
    this.#persistPlace(null);
    this.#persistBusinessContext(null);
    this.#persistRecommendation(null);
    // Drop the owner stamp with the rest of the stash, so it's part of the same
    // lifecycle: once onboarding finishes (clearSelection) or a different account
    // takes over, a later signup in the same tab starts from a clean owner.
    this.#clearOwner();
  }

  #clearOwner(): void {
    try {
      sessionStorage.removeItem(OWNER_STORAGE_KEY);
    } catch {
      // Best-effort; see #persistSelection.
    }
  }

  // Stamp the stash with the account that first consumes it; drop it if a
  // different account later appears in the same tab.
  #bindStashOwner(userId: string): void {
    let owner: string | null;

    try {
      owner = sessionStorage.getItem(OWNER_STORAGE_KEY);
    } catch {
      owner = null;
    }

    if (owner && owner !== userId) {
      this.#clearPersistedState();
    }

    try {
      sessionStorage.setItem(OWNER_STORAGE_KEY, userId);
    } catch {
      // Best-effort; the in-memory signals still gate consumption this session.
    }
  }

  /** Store the reviewed selection for the following onboarding steps to pre-fill from. */
  setSelection(selection: ImportSelection): void {
    this.#selection.set(selection);
    this.#persistSelection(selection);
  }

  /** Stash the selected business's Places address for the post-account department update. */
  setPlace(place: ImportPlaceAddress | null): void {
    this.#place.set(place);
    this.#persistPlace(place);
  }

  /** Stash the Places business context so the onboarding extraction can factor it in. */
  setBusinessContext(business: BusinessContext | null): void {
    this.#businessContext.set(business);
    this.#persistBusinessContext(business);
  }

  /** Remember the staged file so returning to the upload step restores it. */
  setStagedFile(file: File | null): void {
    this.#stagedFile.set(file);
  }

  /** Remember the staged note so returning to the upload step restores it. */
  setStagedNote(note: string): void {
    this.#stagedNote.set(note);
  }

  /** Drop the staged file + note once the import is done, skipped or abandoned. */
  clearStagedUpload(): void {
    this.#stagedFile.set(null);
    this.#stagedNote.set('');
  }

  /** Stash the inferred recurring schedule for the draft-schedule orchestrator to reconcile later. */
  setScheduleRecommendation(recommendation: ScheduleRecommendation | null): void {
    this.#recommendation.set(recommendation);
    this.#persistRecommendation(recommendation);
  }

  /** Discard the stashed recommendation once it has been drafted/accepted/dismissed. */
  clearScheduleRecommendation(): void {
    this.#recommendation.set(null);
    this.#persistRecommendation(null);
  }

  /** Discard the stashed place address once it has been applied. */
  clearPlace(): void {
    this.#place.set(null);
    this.#persistPlace(null);
  }

  /** Discard any pending selection + stashed signup context (e.g. when onboarding finishes). */
  clearSelection(): void {
    this.#selection.set(null);
    this.#persistSelection(null);
    this.clearPlace();
    this.setBusinessContext(null);
    this.clearStagedUpload();
  }

  #readPersistedSelection(): ImportSelection | null {
    try {
      const raw = sessionStorage.getItem(SELECTION_STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as Partial<ImportSelection>;
      // A blob from an earlier deploy (or written before a field existed) can be
      // missing arrays; a consumer's `selection().teams.length` would then throw.
      // Validate the shape once here so every reader gets a safe value or null.
      if (!Array.isArray(parsed.teams) || !Array.isArray(parsed.shiftTemplates) || !Array.isArray(parsed.employees)) {
        return null;
      }

      return parsed as ImportSelection;
    } catch {
      return null;
    }
  }

  #persistSelection(selection: ImportSelection | null): void {
    try {
      if (selection) {
        sessionStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(selection));
      } else {
        sessionStorage.removeItem(SELECTION_STORAGE_KEY);
      }
    } catch {
      // sessionStorage may be unavailable (private mode / quota); the in-memory
      // signal still works for the same-tab SPA navigation.
    }
  }

  #readPersistedPlace(): ImportPlaceAddress | null {
    try {
      const raw = sessionStorage.getItem(PLACE_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as ImportPlaceAddress) : null;
    } catch {
      return null;
    }
  }

  #persistPlace(place: ImportPlaceAddress | null): void {
    try {
      if (place) {
        sessionStorage.setItem(PLACE_STORAGE_KEY, JSON.stringify(place));
      } else {
        sessionStorage.removeItem(PLACE_STORAGE_KEY);
      }
    } catch {
      // See #persistSelection — best-effort; the in-memory signal still works.
    }
  }

  #readPersistedBusinessContext(): BusinessContext | null {
    try {
      const raw = sessionStorage.getItem(BUSINESS_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as BusinessContext) : null;
    } catch {
      return null;
    }
  }

  #persistBusinessContext(business: BusinessContext | null): void {
    try {
      if (business) {
        sessionStorage.setItem(BUSINESS_STORAGE_KEY, JSON.stringify(business));
      } else {
        sessionStorage.removeItem(BUSINESS_STORAGE_KEY);
      }
    } catch {
      // See #persistSelection — best-effort; the in-memory signal still works.
    }
  }

  #readPersistedRecommendation(): ScheduleRecommendation | null {
    try {
      const raw = sessionStorage.getItem(RECOMMENDATION_STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw);
      // Validate the shape once here so a stale blob can't throw in the reconciler.
      return Array.isArray(parsed) ? (parsed as ScheduleRecommendation) : null;
    } catch {
      return null;
    }
  }

  #persistRecommendation(recommendation: ScheduleRecommendation | null): void {
    try {
      if (recommendation) {
        sessionStorage.setItem(RECOMMENDATION_STORAGE_KEY, JSON.stringify(recommendation));
      } else {
        sessionStorage.removeItem(RECOMMENDATION_STORAGE_KEY);
      }
    } catch {
      // See #persistSelection — best-effort; the in-memory signal still works.
    }
  }

  #toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onerror = () => reject(reader.error);
      reader.onload = () => {
        if (typeof reader.result !== 'string') {
          reject(new Error('Could not read the file as a data URL.'));
          return;
        }

        resolve(reader.result.slice(reader.result.indexOf(',') + 1));
      };

      reader.readAsDataURL(file);
    });
  }
}
